import { HttpErrorResponse } from '@angular/common/http';

const REPORTED_TTL_MS = 60_000;
const reported = new Map<string, number>();

/**
 * Records an error message that has already been sent to Bugsnag. `ErrorHandlerService` rethrows
 * HTTP failures as plain strings; when nothing catches the rethrow, it reaches the Angular
 * `ErrorHandler` or Bugsnag's unhandled-rejection hook and would be reported a second time.
 */
export function markReported(message: string): void {
  const now = Date.now();
  reported.forEach((reportedAt, key) => {
    if (now - reportedAt > REPORTED_TTL_MS) {
      reported.delete(key);
    }
  });
  reported.set(message, now);
}

export function isRecentlyReported(message: string): boolean {
  const reportedAt = reported.get(message);
  return reportedAt !== undefined && Date.now() - reportedAt <= REPORTED_TTL_MS;
}

/**
 * True when Bugsnag already has this failure: an `HttpErrorResponse` (ErrorTrackingInterceptor
 * reports every failed request, and a second event would skip its throttle and carry the raw
 * URL and body), one wrapped as a zone `rejection`, or a message `markReported` recorded.
 */
export function isAlreadyReported(error: unknown): boolean {
  const unwrapped = (error as { rejection?: unknown } | null)?.rejection ?? error;
  if (unwrapped instanceof HttpErrorResponse) {
    return true;
  }
  return typeof unwrapped === 'string' && isRecentlyReported(unwrapped);
}

/** For specs. */
export function clearReported(): void {
  reported.clear();
}
